import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckOutlined } from "@ant-design/icons";
import { App, Image, List, Popconfirm, Spin } from "antd";
import { observer } from "mobx-react";
import { useCallback, useState } from "react";
import { useUpsellModalVisualContentBlocker } from "@devowl-wp/react-cookie-banner-admin";
import { isUrl } from "@devowl-wp/react-utils";
import { useStores } from "../../../store/stores.js";
import { __ } from "../../../utils/i18n.js";
import { request } from "../../../utils/request.js";
import { locationRestMigrationPost } from "../../../wp-api/migration.post.js";
import { useProModalMobileExperience } from "../../customize/controls/mobileSwitcher.js";
const DashboardMigrationCardContent = observer(()=>{
    const { message } = App.useApp();
    const [busy, setBusy] = useState(false);
    const { optionStore: { dashboardMigration: { id: migrationId, description, actions }, others: { isPro } } } = useStores();
    const [overrideActions, setOverrideActions] = useState({});
    const [performedActions, setPerformedActions] = useState([]);
    const proModalMobileExperience = useProModalMobileExperience();
    const proModalVisualContentBlocker = useUpsellModalVisualContentBlocker();
    // We do not have this currently in a store / model as it should be only made through this dashboard tile
    const handleClick = useCallback(async ({ id, callback })=>{
        if (typeof callback === "string" && isUrl(callback)) {
            // Directly redirect to this page as there is no server callback
            window.location.href = callback;
        } else {
            setBusy(true);
            try {
                const { success, redirect, message: actionSuccessMessage, overrideAction } = await request({
                    location: locationRestMigrationPost,
                    params: {
                        migration: migrationId,
                        action: id
                    }
                });
                if (success) {
                    if (redirect) {
                        window.location.href = redirect;
                    } else {
                        message.success(actionSuccessMessage || __("Successfully saved changes!"));
                        if (overrideAction) {
                            setOverrideActions((state)=>({
                                    ...state,
                                    [id]: overrideAction
                                }));
                            setPerformedActions((v)=>[
                                    ...v,
                                    id
                                ]);
                        }
                    }
                }
            } catch (e) {
            // Silence is golden. Currently - we do ignore errors
            } finally{
                setBusy(false);
            }
        }
    }, []);
    return /*#__PURE__*/ _jsxs(Spin, {
        spinning: busy,
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "description",
                dangerouslySetInnerHTML: {
                    __html: description
                }
            }),
            /*#__PURE__*/ _jsx(List, {
                itemLayout: "vertical",
                size: "small",
                dataSource: Object.values(actions),
                renderItem: (action)=>{
                    const { id, title, description, linkText, linkClasses, linkDisabled, confirmText, previewImage, performed, performedLabel, needsPro, info } = {
                        ...action,
                        ...overrideActions[action.id] || {},
                        ...performedActions.indexOf(action.id) > -1 ? {
                            performed: true
                        } : {}
                    };
                    const btn = /*#__PURE__*/ _jsx("button", {
                        className: linkClasses,
                        onClick: ()=>!confirmText && handleClick(action),
                        disabled: linkDisabled,
                        children: linkText
                    }, "apply-link");
                    return /*#__PURE__*/ _jsx(List.Item, {
                        actions: [
                            linkText && (confirmText ? /*#__PURE__*/ _jsx(Popconfirm, {
                                title: /*#__PURE__*/ _jsx("span", {
                                    dangerouslySetInnerHTML: {
                                        __html: confirmText
                                    }
                                }),
                                placement: "bottomLeft",
                                onConfirm: ()=>handleClick(action),
                                cancelText: __("Cancel"),
                                okText: linkText,
                                overlayStyle: {
                                    maxWidth: 450
                                },
                                // zoom-in effect does not work correctly within a Card component
                                transitionName: null,
                                children: btn
                            }) : btn),
                            performed && (needsPro ? isPro : true) && /*#__PURE__*/ _jsxs("span", {
                                style: {
                                    verticalAlign: "middle"
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(CheckOutlined, {}),
                                    " ",
                                    performedLabel || __("Already applied")
                                ]
                            }),
                            needsPro && !isPro && (()=>{
                                // TODO: could this be done more generic? currently it's totally ok for this amount of cases
                                let modal;
                                let tag;
                                switch(id){
                                    case "visual-content-blocker":
                                        ({ modal, tag } = proModalVisualContentBlocker);
                                        break;
                                    case "mobile-experience":
                                        ({ modal, tag } = proModalMobileExperience);
                                        break;
                                    default:
                                        return null;
                                }
                                return /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        modal,
                                        "\xa0",
                                        /*#__PURE__*/ _jsx("span", {
                                            style: {
                                                position: "absolute",
                                                top: 3
                                            },
                                            children: tag
                                        })
                                    ]
                                });
                            })()
                        ].filter(Boolean),
                        children: /*#__PURE__*/ _jsx(List.Item.Meta, {
                            title: title,
                            description: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    previewImage ? /* Antd does currently not support webm videos as image source so we create a dummy object which will
                                                    be only used as "click" handler to open the preview. */ previewImage.endsWith(".webm") ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx("video", {
                                                autoPlay: true,
                                                muted: true,
                                                loop: true,
                                                src: previewImage,
                                                style: {
                                                    marginLeft: 15,
                                                    width: 272,
                                                    height: "auto",
                                                    float: "right",
                                                    cursor: "pointer"
                                                },
                                                onClick: (e)=>e.target.nextElementSibling.click(),
                                                children: /*#__PURE__*/ _jsx("source", {
                                                    src: previewImage,
                                                    type: "video/webm"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Image, {
                                                width: 272,
                                                wrapperStyle: {
                                                    display: "none"
                                                },
                                                preview: {
                                                    imageRender: ()=>/*#__PURE__*/ _jsx("video", {
                                                            autoPlay: true,
                                                            muted: true,
                                                            loop: true,
                                                            src: previewImage,
                                                            children: /*#__PURE__*/ _jsx("source", {
                                                                src: previewImage,
                                                                type: "video/webm"
                                                            })
                                                        })
                                                }
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx(Image, {
                                        width: 272,
                                        src: previewImage,
                                        wrapperStyle: {
                                            marginLeft: 15,
                                            float: "right"
                                        }
                                    }) : null,
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        dangerouslySetInnerHTML: {
                                            __html: description
                                        }
                                    }),
                                    !!info && /*#__PURE__*/ _jsx("div", {
                                        className: "notice notice-info below-h2 notice-alt",
                                        style: {
                                            marginTop: 10
                                        },
                                        children: /*#__PURE__*/ _jsx("p", {
                                            className: "description",
                                            dangerouslySetInnerHTML: {
                                                __html: info
                                            }
                                        })
                                    })
                                ]
                            })
                        })
                    }, id);
                }
            })
        ]
    });
});
export { DashboardMigrationCardContent };
